﻿import Element from "../utilities/element";
import { StringExtensions } from "../utilities/extensions";
import "../utilities/readonly";
import $ from 'jquery';
import _ from "underscore";
import "./popover";

declare global {
  interface JQuery {
    realtimevalidation: (option, args) => any;
  }
}


export class RealTimeValidation {
  public static get wireup(): string { return 'dependency'.asDataSelector(); }

  private dependency: Dependency;

  public constructor($element: JQuery) {
    if (!$element && $element.length != 1)
      throw new Error('You must provide a single element to Rustig.RealTimeValidation');

    this.dependency = new Dependency($element);
  }
}

class Dependency {
  private $element: JQuery;
  private element: Element;
  private dependents: Dependent[];
  private _id: string;
  private _name: string;
  private $popover: JQuery;
  private $validationErrorElement: JQuery;
  private shouldPreventPopover: boolean;
  private shouldPreventErrorClass: boolean;

  public get id(): string { return this._id; }
  public get name(): string { return this._name; }
  public get value(): string { return this.element.value(); }

  public constructor($element: JQuery) {
    this.$element = $element;
    this.element = new Element(this.$element);

    this._id = this.$element.data('dependency');
    if (!this.id)
      throw new Error('id cannot be null');
    this._id = this.id.toString().replace(' ', '_');

    this._name = this.$element.data('dependency-name');
    if (!this.name)
      throw new Error('name cannot be null');

    this.shouldPreventPopover = StringExtensions.toBoolean(this.$element.data('dependency-should-prevent-popover'));
    this.shouldPreventErrorClass = StringExtensions.toBoolean(this.$element.data('dependency-should-prevent-error-class'));

    this.dependents = this.getDependents();
    this.$validationErrorElement = this.getValidationErrorElement(this.$element);
    this.$element.on('change.rustig.realtimevalidation', this.onChange.bind(this));
      $(document).one('rustig.conventionsready', () => this.onChange());
  }

  public removeMessageFor(dependent: Dependent): void {
    let $message = dependent.getPopoverMessage();
    if ($message && $message.length) {
      $message.remove();
      dependent.cachePopoverMessage(null);
    }

    let hasOtherErrors = _.any(this.dependents, x => x.hasValidationError);
    if (!hasOtherErrors) {
      this.$validationErrorElement.removeClass('realtimevalidation-error');

      if (this.$validationErrorElement.data('rustig.popover.rtv'))
        this.$validationErrorElement.popover('disable', 'rtv').popover('close', 'rtv');
    }
  }

  public addMessageFor(dependent: Dependent): void {
    if (!this.shouldPreventErrorClass)
      this.$validationErrorElement.addClass('realtimevalidation-error');

    let messageAlreadyExists = dependent.getPopoverMessage();
    if (this.shouldPreventPopover || messageAlreadyExists)
      return;

    if (!this.$popover)
      this.createPopover();

    this.$validationErrorElement.popover('enable', 'rtv');
    this.appendPopoverMessage(dependent);

    if (this.$validationErrorElement.is(':focus'))
      this.$validationErrorElement.popover('open', 'rtv');
  }

  private getValidationErrorElement($element: JQuery): JQuery {
    let $validationElement;

    let target = $element.data('dependency-error-target');
    if (target)
      $validationElement = $('dependency-error-target-for'.asDataSelector(target));
    else
      $validationElement = this.element.$userFacingElement;

    if ($validationElement.length <= 0)
      throw new Error('A validation error element must be defined');

    return $validationElement;
  }

  private getDependents(): Dependent[] {
    let self = this;
    let $dependents = $('[data-depends-on-' + self.id + ']');

    var result: Dependent[] = [];

    $dependents.each((index, value) => {
      result.push(new Dependent($(value), self));
    });

    return result;
  }

  private createPopover(): void {
    let $popoverContent = $('<div data-popover-content="' + this.id + '"></div>')
      .insertAfter(this.$validationErrorElement);

    this.$popover = $('<div class="popover error-popover"></div>')
      .appendTo($popoverContent);

    this.$validationErrorElement.popover({
      popover: this.id,
      trigger: 'focus',
      placement: 'top',
      containerClass: 'error-container',
      channel: 'rtv'
    });
  }

  private appendPopoverMessage(dependent: Dependent) {
    dependent.cachePopoverMessage(
      $('<div>')
        .attr('data-validation-message', 'on')
        .html(this.buildValidationMessage(dependent.validationMessage))
        .appendTo(this.$popover)
    );
  }

  private buildValidationMessage(message: string): string {
    if (this.element.isSelect && message.indexOf("Enter") == 0)
      return "Select" + message.substring("Enter".length);
    return message;
  }

  private onChange(): void {
    for (let i = 0; i < this.dependents.length; i++) {
      this.dependents[i].onChange();
    }
  }
}

class Dependent {
  private element: Element;
  private dependency: Dependency;
  private dependencyValidator: RegExp;
  private dependentValidator: RegExp;
  private _hasValidationError: boolean;
  private shouldDisableDependentWhileValidationError: boolean;
  private _validationMessage: string;

  private get $element(): JQuery { return this.element.$element; }
  private get dependencyValidatorAttribute(): string { return 'data-depends-on-' + this.dependency.id + '-dependency-validator'; }
  private get dependentValidatorAttribute(): string { return 'data-depends-on-' + this.dependency.id + '-dependent-validator'; }
  private get shouldDisableAttribute(): string { return 'data-depends-on-' + this.dependency.id + '-should-disable'; }
  private get validationMessageAttribute(): string { return 'data-depends-on-' + this.dependency.id + '-validation-message'; }

  public get hasValidationError(): boolean { return this._hasValidationError; }
  public get validationMessage(): string { return this._validationMessage; }

  public constructor($element: JQuery, dependency: Dependency) {
    this.element = new Element($element);
    this.dependency = dependency;

    if (this.element.isCheckbox)
      throw new Error('Checkbox is not supported');
    if (this.element.isRadio)
      throw new Error('Radio buttons are not supported');

    let dependentName = $element.data('depends-on-name');
    if (!dependentName)
      throw new Error('dependentName cannot be null');

    let anyValueButEmpty = '.+';
    this.dependentValidator = new RegExp(this.$element.attr(this.dependentValidatorAttribute) || anyValueButEmpty);
    this.dependencyValidator = new RegExp(this.$element.attr(this.dependencyValidatorAttribute) || anyValueButEmpty);

    this._validationMessage = this.$element.attr(this.validationMessageAttribute) || dependency.name + ' is required for ' + dependentName;
    this.shouldDisableDependentWhileValidationError = StringExtensions.toBoolean(this.$element.attr(this.shouldDisableAttribute));

    this.$element.on('change.rustig.realtimevalidation', this.onChange.bind(this));
  }

  public cachePopoverMessage($message: JQuery): void {
    this.$element.data(this.dependency.id, $message);
  }

  public getPopoverMessage(): JQuery {
    return this.$element.data(this.dependency.id);
  }

  public onChange(): void {
    this._hasValidationError = this.dependentPassesValidation() && !this.dependencyPassesValidation();

    if (this.hasValidationError) {
      this.dependency.addMessageFor(this);

      if (this.shouldDisableDependentWhileValidationError) {
        this.element.$userFacingElement.readonly();
        this.element.value('');
      }
    }
    else {
      this.dependency.removeMessageFor(this);

      if (this.shouldDisableDependentWhileValidationError)
        this.element.$userFacingElement.editable();
    }
  }

  private dependentPassesValidation(): boolean {
    return this.dependentValidator.test(this.$element.val() as string);
  }

  private dependencyPassesValidation(): boolean {
    return this.dependencyValidator.test(this.dependency.value);
  }
}

function Plugin(option, args) {
  return this.each(function () {
    var $this = $(this);
    var data = $this.data('pb.realtimevalidation');
    var options = typeof option == 'object' && option;

    if (!data) {
      data = new RealTimeValidation($this);
      $this.data('rustig.realtimevalidation', data);
    }
    if (typeof option == 'string')
      data[option](args);
  });
}

$.fn.realtimevalidation = Plugin;
